import { useCallback, useEffect, useRef, useState } from 'react';

import { MBO_CART_ORIGIN, MBO_POPUP_HEIGHT, MBO_POPUP_WIDTH } from 'app/shared/consts/mbo-destinations';
import { buildMboLaunchUrl } from 'app/shared/utils/build-mbo-launch-url';

/**
 * Mindbody marks every in-widget action that leaves the page with an "open modal" data
 * attribute plus the destination in `data-url`: brandedweb widgets emit
 * `data-bw-widget-open-modal`, the older healcode links emit `data-hc-open-modal`.
 *
 * Deliberately NOT matched:
 *  - `a[target="_blank"]` — the "powered by Mindbody" logo, a genuine outbound link.
 *  - `.healcode-buy-now-popup` — already a deliberate popup on Mindbody's side.
 */
const LAUNCH_TRIGGER_SELECTOR = '[data-bw-widget-open-modal][data-url], [data-hc-open-modal][data-url]';

/** How often we check whether the sign-in window has been closed. */
const POPUP_POLL_MS = 500;

export type MboModalState =
  | { kind: 'closed' }
  | { kind: 'cart'; url: string; label: string }
  | { kind: 'signin'; siteId: string; signinUrl: string; label: string; returnUrl: string };

interface UseMboWidgetModalResult {
  state: MboModalState;
  close: () => void;
  /** Re-open the cart after signing in — also fires automatically when the window closes. */
  resumeCart: () => void;
  /** Re-open the sign-in window if the user dismissed or lost it. */
  reopenSignin: () => void;
}

const buildPopupFeatures = (): string => {
  // `screenX`/`outerWidth` report the top-level browser window even from inside the host's
  // iframe, so the window centres on the user's actual screen position.
  const left = Math.round(window.screenX + (window.outerWidth - MBO_POPUP_WIDTH) / 2);
  const top = Math.round(window.screenY + (window.outerHeight - MBO_POPUP_HEIGHT) / 2);

  // `popup=yes` is what actually forces a window rather than a tab; width/height alone are
  // advisory, which is why Mindbody's own call lands as a tab for some users.
  return `popup=yes,width=${MBO_POPUP_WIDTH},height=${MBO_POPUP_HEIGHT},left=${left},top=${top},scrollbars=yes,resizable=yes`;
};

/**
 * Runs the Mindbody widget's modal flow inside our own dialog.
 *
 * Two takeovers, both capture/first-listener plays against Mindbody's own handlers:
 *
 * 1. **The click.** Mindbody delegates on `body` (jQuery, bubble phase), so a capture-phase
 *    listener on `document` runs first and `stopPropagation` keeps their handler from firing.
 *    Necessary because their handler refuses to use the modal at all once it detects it is in
 *    an iframe (`self != top` on an identity site) and falls back to `window.open`.
 *
 * 2. **The redirect handoff.** When the cart needs the user signed in it posts
 *    `{ cart_path_redirect_url, site_id }` to its embedder. Mindbody's own listener answers
 *    that with `window.location.replace(...)`, which inside the host's iframe would navigate
 *    US to `signin.mindbodyonline.com` — a page that sends `X-Frame-Options: SAMEORIGIN` and
 *    would blow away the miniapp. We register our listener before their script has finished
 *    loading (their `<script>` is appended by `MindbodyWidget` and executes only after a
 *    network fetch, well after this effect runs) and call `stopImmediatePropagation` so theirs
 *    never sees the message. We then send the user to sign in in a separate window and bring
 *    them back to the cart — so, unlike Mindbody's own flow, the page is never navigated away.
 */
export const useMboWidgetModal = (): UseMboWidgetModalResult => {
  const [state, setState] = useState<MboModalState>({ kind: 'closed' });
  const popupRef = useRef<Window | null>(null);
  // Read inside the message listener, which is registered once and must not go stale.
  const stateRef = useRef<MboModalState>(state);

  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  const close = useCallback(() => {
    popupRef.current?.close();
    popupRef.current = null;
    setState({ kind: 'closed' });
  }, []);

  const resumeCart = useCallback(() => {
    const { current } = stateRef;

    if (current.kind !== 'signin') {
      return;
    }

    popupRef.current = null;
    setState({
      kind: 'cart',
      // Mindbody's own script re-enters at `cart_area` after a sign-in round trip. Without a site
      // id that path would be `/sites//cart_area`, so return to the cart we came from instead.
      url: current.siteId ? `${MBO_CART_ORIGIN}/sites/${current.siteId}/cart_area` : current.returnUrl,
      label: current.label,
    });
  }, []);

  const openSigninWindow = useCallback((signinUrl: string) => {
    popupRef.current = window.open(signinUrl, 'mbo-signin', buildPopupFeatures());

    if (!popupRef.current) {
      popupRef.current = window.open(signinUrl, 'mbo-signin');
    }
  }, []);

  const reopenSignin = useCallback(() => {
    const { current } = stateRef;

    if (current.kind === 'signin') {
      openSigninWindow(current.signinUrl);
    }
  }, [openSigninWindow]);

  useEffect(() => {
    const handleClick = (event: MouseEvent): void => {
      // `event.target` can be an inner <svg>/<span>, so walk up to the trigger itself.
      const eventTarget = event.target as Element | null;
      const trigger = typeof eventTarget?.closest === 'function' ? eventTarget.closest(LAUNCH_TRIGGER_SELECTOR) : null;
      const dataUrl = trigger?.getAttribute('data-url');

      if (!dataUrl) {
        return;
      }

      // Resolve BEFORE consuming the click: a destination we can't pin to the cart origin is one
      // Mindbody's own handler should keep, and it must never reach our frame's `src`.
      const url = buildMboLaunchUrl(dataUrl, window.location.href, window.location.search, document.cookie);

      if (!url) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      setState({ kind: 'cart', url, label: trigger?.textContent?.trim() ?? '' });
    };

    const handleMessage = (event: MessageEvent): void => {
      const redirectUrl = event.data?.cart_path_redirect_url;

      if (event.origin !== MBO_CART_ORIGIN || !redirectUrl) {
        return;
      }

      // Keep Mindbody's own listener from navigating the miniapp to a page that refuses to be
      // framed. Only effective because we registered before their script executed.
      event.stopImmediatePropagation();

      const { current } = stateRef;
      const siteId = event.data?.site_id == null ? '' : String(event.data.site_id);
      const returnUrl = current.kind === 'cart' ? current.url : '';

      // With neither a site id nor a cart to return to there is no way back, so leave the cart
      // as it is rather than opening a sign-in the user can't come back from.
      if (!siteId && !returnUrl) {
        return;
      }

      setState({
        kind: 'signin',
        siteId,
        signinUrl: redirectUrl,
        label: current.kind === 'closed' ? '' : current.label,
        returnUrl,
      });
      openSigninWindow(redirectUrl);
    };

    document.addEventListener('click', handleClick, true);
    window.addEventListener('message', handleMessage);

    return () => {
      document.removeEventListener('click', handleClick, true);
      window.removeEventListener('message', handleMessage);
    };
  }, [openSigninWindow]);

  // Bring the user back to the cart as soon as they finish with the sign-in window. Mindbody
  // gives us no completion signal across the origin boundary, so closing is the cue — the
  // dialog also offers an explicit control for when the window is left open.
  useEffect(() => {
    if (state.kind !== 'signin') {
      return undefined;
    }

    const timer = window.setInterval(() => {
      if (popupRef.current?.closed) {
        resumeCart();
      }
    }, POPUP_POLL_MS);

    return () => window.clearInterval(timer);
  }, [state.kind, resumeCart]);

  return { state, close, resumeCart, reopenSignin };
};
